import { Badge, Banner, Button, Dialog, Field, Input } from '@cloudflare/kumo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DangerButton } from '../ui/DangerButton';
import { PasswordField } from '../ui/PasswordField';
import { ICONS } from '../ui/icons';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { DangerConfirmModal } from '../DangerConfirmModal';
import { MetaFlowsModal } from '../MetaFlowsModal';
import { MetaCapiModal } from '../MetaCapiModal';
import { brlFromCents } from '../../lib/tools';
import { SkeletonText } from '../../components/ui/Skeleton';

/** Conexão da WhatsApp Cloud API (GET /api/tools/api-oficial → connections[]) */
export interface MetaConnection {
  name: string; displayName: string | null; phone: string | null; status: string; coexistence: boolean;
  wabaId?: string | null; phoneNumberId?: string | null;
  coexistSyncAt: string | null; coexistSyncAvailable: boolean;
  quality: string | null; verifiedName: string | null; limitTier: string | null; error: string | null;
}
export interface MetaOverview {
  configured: boolean;
  sdk: { appId: string; configId: string; graphVersion: string } | null;
  slots: { total: number; used: number };
  addon: { monthlyCents: number; prepaidQuantity: number; balanceCents: number };
  messagePrices: { MARKETING: number; UTILITY: number; AUTHENTICATION: number };
  connections: MetaConnection[];
}
type SignupSession = { phone_number_id?: string; waba_id?: string; business_id?: string };

declare global {
  interface Window {
    FB?: {
      init: (o: Record<string, unknown>) => void;
      login: (cb: (r: { authResponse?: { code?: string } | null; status?: string }) => void, o: Record<string, unknown>) => void;
    };
    fbAsyncInit?: () => void;
  }
}

const QUALITY: Record<string, { label: string; color: 'green' | 'orange' | 'red' }> = {
  GREEN: { label: 'Qualidade alta', color: 'green' },
  YELLOW: { label: 'Qualidade média', color: 'orange' },
  RED: { label: 'Qualidade baixa', color: 'red' },
};
const TIER: Record<string, string> = { TIER_250: '250 conversas/dia', TIER_1K: '1 mil/dia', TIER_10K: '10 mil/dia', TIER_100K: '100 mil/dia', TIER_UNLIMITED: 'Ilimitado' };

/** Carrega o SDK JavaScript da Meta uma vez */
function loadFacebookSdk(appId: string, version: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.FB) { window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version }); resolve(); return; }
    window.fbAsyncInit = () => { window.FB!.init({ appId, autoLogAppEvents: true, xfbml: false, version }); resolve(); };
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/pt_BR/sdk.js';
    s.async = true;
    s.defer = true;
    s.crossOrigin = 'anonymous';
    s.onerror = () => reject(new Error('Não foi possível carregar o SDK da Meta (verifique bloqueadores de anúncio)'));
    document.body.appendChild(s);
  });
}

const brlMsg = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 4 });

const matches = (c: MetaConnection, q: string) =>
  [c.name, c.displayName, c.verifiedName, c.phone].some((v) => v && v.toLowerCase().includes(q));

/**
 * Conexões da API oficial do WhatsApp (Cloud API da Meta): vagas, Embedded Signup
 * (número novo, coexistência com o app ou migração de outro BSP), token manual e
 * a grade de números conectados. Usado em WhatsApp › Conexões.
 */
export function MetaConnections({ search = '', onLoaded }: { search?: string; onLoaded?: (data: MetaOverview) => void }) {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<MetaOverview | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ phoneNumberId: '', wabaId: '', accessToken: '' });
  const [removing, setRemoving] = useState<MetaConnection | null>(null);
  const [flowsFor, setFlowsFor] = useState<MetaConnection | null>(null);
  const [capiFor, setCapiFor] = useState<MetaConnection | null>(null);
  const [choice, setChoice] = useState<{ sessionId: string; numbers: Array<{ id: string; display_phone_number: string; verified_name: string | null }> } | null>(null);
  const sessionInfo = useRef<SignupSession | null>(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const canManage = can('connections:manage');
  const canBuy = can('billing:manage');

  const load = useCallback(async () => {
    try {
      const res = await fetchApi('/api/tools/api-oficial');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d: MetaOverview = await res.json();
      setData(d);
      setLoadError(false);
      onLoadedRef.current?.(d);
    } catch {
      setLoadError(true);
    }
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  // Contrata mais uma vaga paga com créditos (30 dias) e libera o Embedded Signup
  const hireSlot = async () => {
    if (!data) return;
    setBusy('hire');
    try {
      const res = await fetchApi('/api/tools/addons/META_API/credits', { method: 'PUT', body: JSON.stringify({ quantity: data.addon.prepaidQuantity + 1 }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível contratar');
      toast.success('Vaga contratada. Agora conecte o número pela Meta.');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível contratar');
    } finally {
      setBusy(null);
    }
  };

  // Dados do número/WABA chegam por mensagem da janela da Meta (Embedded Signup)
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!event.origin.endsWith('facebook.com')) return;
      try {
        const msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (msg?.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (String(msg.event).startsWith('FINISH')) sessionInfo.current = msg.data;
        else if (msg.event === 'CANCEL') setError(msg.data?.error_message ? `A Meta interrompeu o cadastro: ${msg.data.error_message}` : null);
      } catch { /* mensagem de outro tipo */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // cloud: número novo na API · coex: WhatsApp Business app + API · migrate: número que já está em outro provedor (BSP)
  const connect = async (mode: 'cloud' | 'coex' | 'migrate') => {
    if (!data?.sdk) return;
    setError(null);
    setBusy(mode);
    sessionInfo.current = null;
    const coexistence = mode === 'coex';
    try {
      await loadFacebookSdk(data.sdk.appId, data.sdk.graphVersion);
      const featureType = mode === 'coex' ? 'whatsapp_business_app_onboarding' : mode === 'migrate' ? 'only_waba_sharing' : undefined;
      const code = await new Promise<string>((resolve, reject) => {
        window.FB!.login((r) => {
          if (r.authResponse?.code) resolve(r.authResponse.code);
          else reject(new Error('Cadastro cancelado'));
        }, {
          config_id: data.sdk!.configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: { setup: {}, sessionInfoVersion: '3', ...(featureType ? { featureType } : {}) },
        });
      });
      // O código vale 30 s; os IDs chegam pela mensagem da janela (espera até 5 s). Sem eles,
      // o servidor descobre a WABA pelo token e pede para escolher o número.
      const readInfo = (): SignupSession | null => sessionInfo.current;
      for (let i = 0; i < 25 && !readInfo()?.waba_id; i++) await new Promise((r) => setTimeout(r, 200));
      const info = readInfo();
      const res = await fetchApi('/api/tools/api-oficial/embedded-signup', {
        method: 'POST',
        body: JSON.stringify({ code, phoneNumberId: info?.phone_number_id, wabaId: info?.waba_id, businessId: info?.business_id, coexistence }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível concluir a conexão');
      if (body.needsChoice) { setChoice({ sessionId: body.sessionId, numbers: body.numbers }); return; }
      toast.success(`${body.connection.displayName} conectado pela API oficial`);
      load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Não foi possível conectar';
      if (msg !== 'Cadastro cancelado') setError(msg);
    } finally {
      setBusy(null);
    }
  };

  const chooseNumber = async (phoneNumberId: string) => {
    if (!choice) return;
    setBusy('choose');
    try {
      const res = await fetchApi('/api/tools/api-oficial/embedded-signup/choose', { method: 'POST', body: JSON.stringify({ sessionId: choice.sessionId, phoneNumberId }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível concluir a conexão');
      toast.success(`${body.connection.displayName} conectado pela API oficial`);
      setChoice(null);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível conectar');
      setChoice(null);
    } finally {
      setBusy(null);
    }
  };

  const coexistSync = async (c: MetaConnection) => {
    setBusy(`sync-${c.name}`);
    const res = await fetchApi(`/api/tools/api-oficial/${encodeURIComponent(c.name)}/coexist-sync`, { method: 'POST' });
    const body = await res.json().catch(() => ({}));
    setBusy(null);
    if (!res.ok) { toast.error(body.message || 'Não foi possível importar'); return; }
    toast.success('Importação pedida: os contatos e conversas do app chegam nos próximos minutos');
    load();
  };

  const saveManual = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('manual');
    try {
      const res = await fetchApi('/api/tools/api-oficial/manual', { method: 'POST', body: JSON.stringify(manual) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível conectar');
      toast.success(`${body.connection.displayName} conectado`);
      setManualOpen(false);
      setManual({ phoneNumberId: '', wabaId: '', accessToken: '' });
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível conectar');
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    const c = removing;
    setRemoving(null);
    if (!c) return;
    const res = await fetchApi(`/api/tools/api-oficial/${encodeURIComponent(c.name)}`, { method: 'DELETE' });
    if (res.ok) toast.success('Conexão removida'); else toast.error('Não foi possível remover');
    load();
  };

  if (!data) {
    if (loadError) {
      return (
        <Banner variant="error" description={<>
          Não foi possível carregar as conexões do WhatsApp. <button type="button" className="auth-link" onClick={load}>Tentar de novo</button>
        </>} />
      );
    }
    return <div className="py-8"><SkeletonText lines={4} /></div>;
  }

  const full = data.slots.used >= data.slots.total;
  const q = search.trim().toLowerCase();
  const visible = q ? data.connections.filter((c) => matches(c, q)) : data.connections;

  return (
    <>
      <div className="space-y-6">
        {!data.configured && <Banner variant="default" description={<>A API oficial ainda não está ativa na plataforma.</>} />}
        {error && <Banner variant="error" description={<>{error}</>} />}

        <div className="tools-stats">
          <div className="tools-stat"><small>Números conectados</small><strong>{data.slots.used} de {data.slots.total}</strong>
            <Link to="/ferramentas#addons" className="tools-stat__link">{data.slots.total === 0 ? 'Contratar o add-on' : 'Alterar quantidade'}</Link>
          </div>
          <div className="tools-stat"><small>Custo por mensagem (Meta, Brasil)</small><strong style={{ fontSize: 18 }}>{brlMsg(data.messagePrices.MARKETING)} marketing</strong><span className="tools-stat__hint">{brlMsg(data.messagePrices.UTILITY)} utilidade e autenticação</span></div>
        </div>

        {data.configured && canManage && (
          <section className="brand-card">
            <h4 className="text-base font-semibold">Conectar um número</h4>
            <div style={{ color: 'var(--text-muted)', marginTop: 2, marginBottom: 16 }}>
              Uma janela da Meta abre para você entrar com o Facebook, escolher (ou criar) a conta empresarial e o número. Ao terminar, a conexão aparece aqui.
            </div>
            {full && (
              <div className="meta-hire">
                <div>
                  <strong>{data.slots.total === 0 ? 'Contrate uma vaga para conectar pela Meta' : `As ${data.slots.total} vagas estão em uso`}</strong>
                  <span>
                    Cada número na API oficial usa uma vaga: {brlFromCents(data.addon.monthlyCents)} por 30 dias, pagos com os créditos das ferramentas
                    (saldo {brlFromCents(data.addon.balanceCents)}). Depois de contratar, os botões abaixo são liberados.
                  </span>
                </div>
                {canBuy && (data.addon.balanceCents >= data.addon.monthlyCents ? (
                  <Button variant="primary" icon={ICONS.Plus} loading={busy === 'hire'} onClick={hireSlot}>
                    {data.slots.total === 0 ? 'Contratar' : 'Contratar mais 1'} por {brlFromCents(data.addon.monthlyCents)}
                  </Button>
                ) : (
                  <Link to="/ferramentas#creditos"><Button type="submit" variant="primary" icon={ICONS.Plus}>Comprar créditos</Button></Link>
                ))}
              </div>
            )}
            <div className="billing-methods" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              <button type="button" className="billing-method" disabled={!!busy || full} onClick={() => connect('cloud')}>
                <ICONS.Shield size={24} />
                <span><strong>Número novo na API</strong><small>O número passa a funcionar só pela API (não pode estar ativo no app)</small></span>
                {busy === 'cloud' && <span className="billing-method__spinner" aria-hidden />}
              </button>
              <button type="button" className="billing-method" disabled={!!busy || full} onClick={() => connect('coex')}>
                <ICONS.Mobile size={24} />
                <span><strong>Usar o WhatsApp Business app (coexistência)</strong><small>O número continua no celular e também envia pela API</small></span>
                {busy === 'coex' && <span className="billing-method__spinner" aria-hidden />}
              </button>
              <button type="button" className="billing-method" disabled={!!busy || full} onClick={() => connect('migrate')}>
                <ICONS.Import size={24} />
                <span><strong>Trazer de outro provedor</strong><small>O número já está na API por outra empresa (BSP) e passa a ser gerido aqui</small></span>
                {busy === 'migrate' && <span className="billing-method__spinner" aria-hidden />}
              </button>
            </div>
            <button type="button" className="auth-link" style={{ marginTop: 12 }} disabled={full} onClick={() => setManualOpen(true)}>Já tenho o token de um usuário do sistema</button>
          </section>
        )}

        {data.connections.length === 0 ? (
          <div className="connection-empty">
            <ICONS.SMS size={24} />
            <h4 className="text-base font-semibold">Nenhum número conectado</h4>
            <div style={{ color: 'var(--text-muted)' }}>Conecte um número pela API oficial da Meta para começar a enviar.</div>
          </div>
        ) : visible.length === 0 ? (
          <div className="connection-empty">
            <ICONS.MagnifyingGlass size={24} />
            <h4 className="text-base font-semibold">Nenhum número encontrado</h4>
            <div style={{ color: 'var(--text-muted)' }}>Nenhum número do WhatsApp corresponde à busca.</div>
          </div>
        ) : (
          <div className="health-grid">
            {visible.map((c) => {
              const quality = c.quality ? QUALITY[c.quality] : null;
              return (
                <section key={c.name} className="health-card">
                  <div className="flex items-start justify-between gap-2">
                    <div style={{ minWidth: 0 }}>
                      <div className="font-semibold" style={{ fontSize: 15 }}>{c.verifiedName || c.displayName || c.name}</div>
                      {c.phone && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>+{c.phone}</div>}
                    </div>
                    {quality ? <Badge variant={quality.color}>{quality.label}</Badge> : <Badge variant="neutral">Sem avaliação</Badge>}
                  </div>
                  <div className="flex gap-2 flex-wrap" style={{ marginTop: 12 }}>
                    {c.coexistence && <Badge variant="blue">Coexistência com o app</Badge>}
                    {c.limitTier && <Badge variant="neutral">Limite: {TIER[c.limitTier] || c.limitTier}</Badge>}
                  </div>
                  {c.error && <div style={{ marginTop: 12 }}><Banner variant="error" description={<>{c.error}</>} /></div>}
                  {c.coexistSyncAvailable && canManage && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Traga os contatos e as conversas do WhatsApp Business app. A Meta só permite uma vez, até 24 h depois de conectar.</div>
                      <Button variant="secondary" loading={busy === `sync-${c.name}`} onClick={() => coexistSync(c)}>Importar contatos e conversas do app</Button>
                    </div>
                  )}
                  {c.coexistSyncAt && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>Importação do app pedida em {new Date(c.coexistSyncAt).toLocaleString('pt-BR')}</div>}
                  <div className="flex gap-2 flex-wrap" style={{ marginTop: 16 }}>
                    <Button variant="secondary" icon={ICONS.Checkmark} onClick={() => setFlowsFor(c)}>Formulários</Button>
                    <Button variant="secondary" icon={ICONS.Charts} onClick={() => setCapiFor(c)}>Conversões</Button>
                    {canManage && <DangerButton variant="dangerOutline" onClick={() => setRemoving(c)}>Remover</DangerButton>}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      <Dialog.Root open={manualOpen} onOpenChange={(o: boolean) => { if (!o) (() => setManualOpen(false))(); }}>
<Dialog size="base" className="p-6">


        <form noValidate onSubmit={saveManual} className="flex flex-col gap-4">
          <h3 className="text-lg font-semibold">Conectar com token</h3>
          <div style={{ color: 'var(--text-muted)' }}>No Gerenciador do WhatsApp (business.facebook.com), copie o ID do número, o ID da conta do WhatsApp Business e gere um token permanente de usuário do sistema com as permissões whatsapp_business_messaging e whatsapp_business_management.</div>
          <Field label="ID do número de telefone (Phone Number ID)">
            <Input onChange={(e) => setManual({ ...manual, phoneNumberId: e.target.value.trim() })} value={manual.phoneNumberId} />
          </Field>
          <Field label="ID da conta do WhatsApp Business (WABA ID)">
            <Input onChange={(e) => setManual({ ...manual, wabaId: e.target.value.trim() })} value={manual.wabaId} />
          </Field>
          <PasswordField label="Token de acesso" value={manual.accessToken}
            onChange={(value) => setManual({ ...manual, accessToken: value.trim() })} autoComplete="off" />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setManualOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={busy === 'manual'} disabled={!manual.phoneNumberId || !manual.wabaId || !manual.accessToken}>Conectar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      {capiFor && (
        <MetaCapiModal sessionName={capiFor.name} label={capiFor.verifiedName || capiFor.displayName || capiFor.name} canManage={canManage} onClose={() => setCapiFor(null)} />
      )}

      {flowsFor && (
        <MetaFlowsModal sessionName={flowsFor.name} label={flowsFor.verifiedName || flowsFor.displayName || flowsFor.name} canEdit={can('campaigns:write')} onClose={() => setFlowsFor(null)} />
      )}

      <Dialog.Root open={!!choice} onOpenChange={(o: boolean) => { if (!o) (() => setChoice(null))(); }}>
<Dialog size="base" className="p-6">


        <h3 className="text-lg font-semibold" style={{ marginBottom: 8 }}>Qual número conectar?</h3>
        <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>Esta conta do WhatsApp Business tem mais de um número. Escolha o que vai usar aqui (cada número ocupa uma vaga).</div>
        <div className="space-y-2">
          {choice?.numbers.map((n) => (
            <button key={n.id} type="button" className="billing-method w-full" disabled={busy === 'choose'} onClick={() => chooseNumber(n.id)}>
              <ICONS.Phone size={32} />
              <span><strong>{n.display_phone_number}</strong><small>{n.verified_name || 'Nome ainda não verificado'}</small></span>
            </button>
          ))}
        </div>
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal open={removing !== null} title="Remover conexão" confirmText="Remover" objectName={removing?.verifiedName || removing?.displayName || removing?.name} onConfirm={remove} onCancel={() => setRemoving(null)}>
        A plataforma deixa de enviar por {removing?.verifiedName || removing?.displayName || removing?.name}. O número continua na sua conta da Meta. Campanhas que usam esta conexão deixam de enviar por ela.
      </DangerConfirmModal>
    </>
  );
}
